/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Action, Actions, NgxsOnInit, ofActionSuccessful, Selector, State, Store } from '@ngxs/store';
import { StateContext } from '@ngxs/store/src/symbols';
import {
    LoadRedlineConfig,
    RedlineVisibilityChanged,
    SetRedlineLayer,
    TurnRedlineLayerOff,
    TurnRedlineLayerOn,
} from './redline.actions';
import { RestService } from '../../../modules/shared/services/rest.service';
import { catchError, concatMap, distinctUntilChanged, filter, mergeMap, mergeMapTo, take, tap } from 'rxjs/operators';
import { RedlineLayerConfiguration, RedlineStateModel } from './redline.model';
import { AppConfigState } from '../../../core/state/appConfig/app-config.state';
import { DashGisUtil } from '../../../modules/shared/dash-gis-util';
import { SetMapZoom } from '../../../modules/map/state/active-map.actions';
import { LoggerService } from '../../../modules/shared/services/logger.service';
import { ActiveMapState } from '../../../modules/map/state/active-map.state';
import { AuthState } from '../../../core/security/state/authState.state';
import { Injectable } from '@angular/core';
import { of } from 'rxjs';

@State<RedlineStateModel>({
    name: 'redlineConfig',
    defaults: {
        loaded: false,
        view: [],
        edit: [],
        currentLayer: '',
    },
})
@Injectable()
export class RedlineState implements NgxsOnInit {

    constructor(
        private logger: LoggerService,
        private rest: RestService,
        private actions$: Actions,
        private store: Store
    ) {}

    ngxsOnInit(ctx: StateContext<any>) {
        this.store
            .select(ActiveMapState.created)
            .pipe(
                catchError((err) => {
                    this.logger.error('redline.state.ts:54', err);
                    return of(false);
                }),
                distinctUntilChanged(),
                filter((b) => b),
                mergeMapTo(this.store.select(AuthState.validated)),
                distinctUntilChanged(),
                filter((b) => b),
                mergeMap(() => this.store.select(AppConfigState.currentMap)),
                filter((b) => !!b),
                concatMap((mapName: string) => ctx.dispatch(new LoadRedlineConfig(mapName)))
            )
            .subscribe();

        this.actions$
            .pipe(
                ofActionSuccessful(SetMapZoom),
                mergeMap(() => this.store.select(RedlineState.loaded)),
                take(1),
                filter((b) => b)
            )
            .subscribe(() => {
                ctx.dispatch(new RedlineVisibilityChanged());
            });
    }

    @Action(LoadRedlineConfig)
    loadRedlineConfig({ getState, setState, dispatch }: StateContext<RedlineStateModel>, action: LoadRedlineConfig) {
        return this.rest.apiGet(`map/redline/config/${action.mapName}`).pipe(
            tap((config: RedlineStateModel) => {
                if (config) {
                    const state = DashGisUtil.clone(getState());
                    state.view = config.view;
                    state.edit = config.edit;
                    state.loaded = true;
                    setState(state);
                }
            })
        );
    }

    @Action(SetRedlineLayer)
    setRedlineLayer({ getState, setState }, action: SetRedlineLayer) {
        const state: RedlineStateModel = DashGisUtil.clone(getState());
        state.currentLayer = action.layerName;
        setState(state);
    }

    @Action(TurnRedlineLayerOn)
    turnRedlineLayerOn(ctx: StateContext<RedlineStateModel>, action: TurnRedlineLayerOn) {
        const state: RedlineStateModel = DashGisUtil.clone(ctx.getState());
        const layer = state.view.find(
            (redlineLayer: RedlineLayerConfiguration) => redlineLayer.name === action.layerName
        );
        if (layer) {
            layer.status = true;
        }
        ctx.setState(state);
        ctx.dispatch(new RedlineVisibilityChanged());
    }

    @Action(TurnRedlineLayerOff)
    turnRedlineLayerOff(ctx: StateContext<RedlineStateModel>, action: TurnRedlineLayerOff) {
        const state: RedlineStateModel = DashGisUtil.clone(ctx.getState());
        const layer = state.view.find((l: RedlineLayerConfiguration) => l.name === action.layerName);
        if (layer) {
            layer.status = false;
        }
        ctx.setState(state);
        ctx.dispatch(new RedlineVisibilityChanged());
    }

    @Action(RedlineVisibilityChanged)
    redlineVisibilityChanged() {}

    @Selector() static loaded(state: RedlineStateModel) {
        return state.loaded;
    }
    @Selector() static viewable(state: RedlineStateModel) {
        return state.view;
    }
    @Selector() static editable(state: RedlineStateModel) {
        return state.edit;
    }
    @Selector() static current(state: RedlineStateModel) {
        return state.currentLayer;
    }
}
